import { Box, HStack, Input, Text, VStack } from "@chakra-ui/react";
import React from "react";
import { entityProfileModalColors } from "../../../../../entityProfileModal/entityProfileModal.theme";
import type { PatientProfileBridgeActions } from "../../../../patientProfileModal.actions";
import { formatPatientProfileValue } from "../../../../patientProfileModal.utils";
import type {
  FamilyInformationField,
  FamilyInformationFieldKey,
  FamilyInformationMember,
} from "../patientInformation.types";
import { formatFamilyPhoneNumber, nullableFamilyText } from "../patientInformation.utils";
import {
  InformationBlock,
  InformationEditableRow,
  InformationReadonlyRow,
} from "./InformationRows";
import EntityModalButton from "../../../../../entityProfileModal/components/EntityModalButton";

function CallFamilyPhoneValue(props: {
  actions: PatientProfileBridgeActions;
  label: string;
  phoneNumber: FamilyInformationMember["mobilePhoneNumber"];
}) {
  if (props.phoneNumber === null || props.phoneNumber === undefined || props.phoneNumber === "") {
    return <>{formatFamilyPhoneNumber(props.phoneNumber)}</>;
  }

  return (
    <HStack justifyContent="flex-end" spacing={1}>
      <span>{formatFamilyPhoneNumber(props.phoneNumber)}</span>
      <EntityModalButton
        isIconOnly
        aria-label={`Call ${props.label}`}
        iconClassName="fa fa-phone"
        kind="white"
        size="xs"
        title="Call"
        onClick={() => props.actions.openDialer({ predefinedPhoneNumber: props.phoneNumber! })}
      />
    </HStack>
  );
}

export function FamilyInformationBlock(props: {
  actions: PatientProfileBridgeActions;
  activeFamilyField: FamilyInformationFieldKey | null;
  familyMembers: FamilyInformationMember[] | null | undefined;
  isOnboardingAgency: boolean;
  isEditing: boolean;
  onActivateField: (field: FamilyInformationFieldKey) => void;
  onAddFamilyMember: () => void;
  onCancelField: () => void;
  onCommitField: () => void;
  onUpdateFamilyMember: (
    index: number,
    field: FamilyInformationField,
    value: FamilyInformationMember[FamilyInformationField]
  ) => void;
}) {
  return (
    <InformationBlock title="Family Information">
      {props.isEditing ? (
        <HStack justifyContent="flex-end" mb={2}>
          <EntityModalButton
            iconClassName="fa fa-plus"
            kind="white"
            size="sm"
            onClick={props.onAddFamilyMember}
          >
            Add family member
          </EntityModalButton>
        </HStack>
      ) : null}
      {props.familyMembers === null ||
      props.familyMembers === undefined ||
      props.familyMembers.length === 0 ? (
        <InformationReadonlyRow label="Family members" value="None" />
      ) : (
        props.familyMembers.map((member, index) => (
          <FamilyMemberCard
            key={index}
            actions={props.actions}
            activeFamilyField={props.activeFamilyField}
            index={index}
            isEditing={props.isEditing}
            isOnboardingAgency={props.isOnboardingAgency}
            member={member}
            onActivateField={props.onActivateField}
            onCancelField={props.onCancelField}
            onCommitField={props.onCommitField}
            onUpdateFamilyMember={props.onUpdateFamilyMember}
          />
        ))
      )}
    </InformationBlock>
  );
}

function FamilyMemberCard(props: {
  actions: PatientProfileBridgeActions;
  activeFamilyField: FamilyInformationFieldKey | null;
  index: number;
  isOnboardingAgency: boolean;
  isEditing: boolean;
  member: FamilyInformationMember;
  onActivateField: (field: FamilyInformationFieldKey) => void;
  onCancelField: () => void;
  onCommitField: () => void;
  onUpdateFamilyMember: (
    index: number,
    field: FamilyInformationField,
    value: FamilyInformationMember[FamilyInformationField]
  ) => void;
}) {
  const renderFamilyEditableRow = (
    field: FamilyInformationField,
    label: string,
    value: React.ReactNode
  ) => {
    const fieldKey = `familyInformation.${props.index}.${field}` as FamilyInformationFieldKey;

    return (
      <InformationEditableRow
        activeField={props.activeFamilyField}
        field={fieldKey}
        isEditing={props.isEditing}
        label={label}
        value={value}
        onActivate={props.onActivateField}
        onCancel={props.onCancelField}
        onCommit={props.onCommitField}
      >
        <Input
          autoFocus
          size="sm"
          value={String(props.member[field] ?? "")}
          onChange={(event) =>
            props.onUpdateFamilyMember(props.index, field, nullableFamilyText(event.target.value))
          }
        />
      </InformationEditableRow>
    );
  };

  return (
    <Box p={3}>
      <Text color={entityProfileModalColors.text} fontSize="14px" fontWeight={700} mb={2}>
        Family Member {props.index + 1}
      </Text>
      <VStack alignItems="stretch" spacing={2}>
        {renderFamilyEditableRow(
          "firstName",
          "First Name",
          formatPatientProfileValue(props.member.firstName)
        )}
        {renderFamilyEditableRow(
          "lastName",
          "Last Name",
          formatPatientProfileValue(props.member.lastName)
        )}
        {renderFamilyEditableRow(
          "relationship",
          "Relationship",
          formatPatientProfileValue(props.member.relationship)
        )}
        {renderFamilyEditableRow(
          "mobilePhoneNumber",
          "Mobile Phone",
          props.isEditing ? (
            formatFamilyPhoneNumber(props.member.mobilePhoneNumber)
          ) : props.isOnboardingAgency ? (
            <CallFamilyPhoneValue
              actions={props.actions}
              label="mobile phone"
              phoneNumber={props.member.mobilePhoneNumber}
            />
          ) : (
            formatFamilyPhoneNumber(props.member.mobilePhoneNumber)
          )
        )}
        {renderFamilyEditableRow(
          "homePhoneNumber",
          "Home Phone",
          props.isEditing ? (
            formatFamilyPhoneNumber(props.member.homePhoneNumber)
          ) : props.isOnboardingAgency ? (
            <CallFamilyPhoneValue
              actions={props.actions}
              label="home phone"
              phoneNumber={props.member.homePhoneNumber}
            />
          ) : (
            formatFamilyPhoneNumber(props.member.homePhoneNumber)
          )
        )}
      </VStack>
    </Box>
  );
}
